import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import axios from "axios";
import { getAdminToken, API_BASE } from "../lib/api";
import SmartImage from "./SmartImage";

/**
 * <ImageUploader value={url} onChange={(url) => ...} label="Image" testId="..." />
 * Uploads to /api/admin/upload (GridFS), returns the URL `/api/files/{id}`.
 */
const ImageUploader = ({ value = "", onChange, label = "Image", testId = "image-uploader" }) => {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            return toast.error("File too large (max 8 MB)");
        }
        if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
            return toast.error("Only JPG, PNG and WEBP images are accepted");
        }
        const fd = new FormData();
        fd.append("file", file);
        const token = getAdminToken();

        setBusy(true);
        try {
            const r = await axios.post(`${API_BASE}/admin/upload`, fd, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            onChange(r.data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={testId}>
            {label && <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}</label>}
            <div className="mt-1 flex items-center gap-3">
                <div className="w-20 h-20 rounded-lg bg-cream-100 border border-cream-200 overflow-hidden shrink-0 flex items-center justify-center">
                    {value ? (
                        <SmartImage src={value} alt="Preview" className="w-full h-full" />
                    ) : (
                        <ImageIcon className="w-6 h-6 text-ink-softer" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cream-200 hover:border-saffron-300 hover:bg-saffron-50 text-xs font-semibold transition disabled:opacity-50"
                            data-testid={`${testId}-pick`}
                        >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            Upload
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cream-200 hover:border-red-300 hover:bg-red-50 text-xs text-red-600 font-semibold transition"
                                data-testid={`${testId}-clear`}
                            >
                                <X className="w-3.5 h-3.5" /> Remove
                            </button>
                        )}
                    </div>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="…or paste an image URL"
                        className="mt-2 w-full px-2.5 py-1.5 rounded-md border border-cream-200 text-xs bg-white"
                        data-testid={`${testId}-url`}
                    />
                </div>
            </div>
            <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid={`${testId}-file`}
            />
        </div>
    );
};

export default ImageUploader;
